import {
  actualStateDisplayLabel,
  isSubmittedActualState,
  normalizeActualState,
} from "../../utils/shiftActualState";
import {
  formatDurationAfterBreak,
  formatShiftDateTime,
  normalizeBreakMinutes,
} from "../../utils/shiftListStatusDisplay";
import {
  isActualColumnUnassigned,
  resolveOutsourcedPayerId,
  resolveShiftStaffAccountId,
  shiftHasRealStaff,
} from "../../utils/shiftStaffDisplay";
import "./ShiftActualTimeSubmissionCell.css";

const ACTUAL_CHIP_COLORS = {
  as_scheduled: "#4ea45b",
  asscheduled: "#4ea45b",
  different: "#c9b030",
  no_show: "#8e77af",
  noshow: "#8e77af",
};

function resolveActualStateRaw(shift) {
  return (
    shift?.actualState ||
    shift?.actual_state ||
    shift?.chosenActualState ||
    shift?.actual_state_text ||
    ""
  );
}

function resolveScheduleStart(shift) {
  return (
    shift?.startDateIso ||
    shift?.start_date ||
    shift?.startDate ||
    shift?.start_time ||
    null
  );
}

function resolveScheduleEnd(shift) {
  return (
    shift?.endDateIso ||
    shift?.end_date ||
    shift?.endDate ||
    shift?.end_time ||
    null
  );
}

function resolveFirstPositiveBreakMinutes(...values) {
  for (const value of values) {
    const minutes = normalizeBreakMinutes(value);
    if (minutes > 0) return minutes;
  }
  return 0;
}

function resolveAsScheduledBreakMinutes(shift) {
  return resolveFirstPositiveBreakMinutes(
    shift?.unpaid_break,
    shift?.unpaidBreak,
  );
}

function resolveSubmittedBreakMinutes(shift, actualStateNorm) {
  if (actualStateNorm === "as_scheduled" || actualStateNorm === "asscheduled") {
    return resolveAsScheduledBreakMinutes(shift);
  }

  const rawActual =
    shift?.actualUnpaidBreakMinutes ?? shift?.actual_unpaid_break_minutes;
  if (rawActual !== undefined && rawActual !== null && rawActual !== "") {
    const n = Number(rawActual);
    if (Number.isFinite(n) && n >= 0) {
      return n;
    }
  }

  return resolveFirstPositiveBreakMinutes(
    shift?.checkinUnpaidBreakMinutes,
    shift?.checkin_unpaid_break_minutes,
    shift?.unpaidBreakMinutes,
    shift?.unpaid_break_minutes,
    shift?.unpaid_break,
    shift?.unpaidBreak,
  );
}

export default function ShiftActualTimeSubmissionCell({
  shift,
  canWrite = false,
  isSubmitting = false,
  onSubmitAsScheduled,
  onSubmitNoShow,
  onOpenDifferent,
  onModifySubmitted,
}) {
  const actualStateRaw = resolveActualStateRaw(shift);
  const actualStateNorm = normalizeActualState(actualStateRaw);
  const shiftState = String(shift?.shiftState || shift?.state || "")
    .replace(/[\s_]+/g, "")
    .toLowerCase();

  if (isActualColumnUnassigned(shift, actualStateRaw)) {
    return (
      <span className="shift-actual-cell__unassigned">Unassigned</span>
    );
  }

  const accountId = resolveShiftStaffAccountId(shift);
  const outsourcedPayerId = resolveOutsourcedPayerId(shift);
  const payerAssignedOnly = outsourcedPayerId && !accountId;

  if (payerAssignedOnly || !canWrite) {
    if (isSubmittedActualState(actualStateRaw)) {
      return (
        <SubmittedActualDisplay
          shift={shift}
          actualStateNorm={actualStateNorm}
          actualStateRaw={actualStateRaw}
          clickable={false}
          onModify={onModifySubmitted}
        />
      );
    }
    return (
      <span className="shift-actual-cell__not-submitted">Not submitted</span>
    );
  }

  if (["cancelled", "declined", "deleted"].includes(shiftState)) {
    return null;
  }

  if (!shiftHasRealStaff(shift)) {
    return (
      <span className="shift-actual-cell__unassigned">Unassigned</span>
    );
  }

  if (isSubmittedActualState(actualStateRaw)) {
    return (
      <SubmittedActualDisplay
        shift={shift}
        actualStateNorm={actualStateNorm}
        actualStateRaw={actualStateRaw}
        clickable={canWrite}
        onModify={onModifySubmitted}
      />
    );
  }

  if (!accountId) {
    return (
      <span className="shift-actual-cell__not-submitted">Not submitted</span>
    );
  }

  return (
    <div className="shift-actual-cell">
      <div className="shift-actual-cell__buttons">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onSubmitAsScheduled}
          className="shift-actual-cell__btn"
        >
          {isSubmitting ? "Saving…" : "As Scheduled"}
        </button>
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onOpenDifferent}
          className="shift-actual-cell__btn"
        >
          Different
        </button>
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onSubmitNoShow}
          className="shift-actual-cell__btn"
        >
          No Show
        </button>
      </div>
    </div>
  );
}

function SubmittedActualDisplay({
  shift,
  actualStateNorm,
  actualStateRaw,
  clickable,
  onModify,
}) {
  const actualStart =
    shift.actualStartDate ||
    shift.actualStartDateIso ||
    shift.actual_start_date ||
    null;
  const actualEnd =
    shift.actualEndDate ||
    shift.actualEndDateIso ||
    shift.actual_end_date ||
    null;
  const actualBreak = resolveSubmittedBreakMinutes(shift, actualStateNorm);
  const scheduleStart = resolveScheduleStart(shift);
  const scheduleEnd = resolveScheduleEnd(shift);

  const showTimes =
    actualStateNorm === "as_scheduled" ||
    actualStateNorm === "asscheduled" ||
    actualStateNorm === "different";

  const displayStart =
    actualStart ||
    (actualStateNorm === "as_scheduled" || actualStateNorm === "asscheduled"
      ? scheduleStart
      : null);
  const displayEnd =
    actualEnd ||
    (actualStateNorm === "as_scheduled" || actualStateNorm === "asscheduled"
      ? scheduleEnd
      : null);
  const displayBreak = actualBreak;

  const chipColor =
    ACTUAL_CHIP_COLORS[actualStateNorm] || ACTUAL_CHIP_COLORS.as_scheduled;
  const chipLabel = actualStateDisplayLabel(actualStateRaw);

  return (
    <div className="shift-actual-cell">
      {showTimes && (displayStart || displayEnd) && (
        <div className="shift-actual-cell__times">
          <span className="text-foreground">
            {displayStart && displayEnd
              ? `${formatShiftDateTime(displayStart)} - ${formatShiftDateTime(displayEnd, false)}`
              : displayStart
                ? formatShiftDateTime(displayStart)
                : displayEnd
                  ? formatShiftDateTime(displayEnd, false)
                  : ""}
          </span>
          <span className="shift-actual-cell__duration">
            {formatDurationAfterBreak(
              displayStart || scheduleStart,
              displayEnd || scheduleEnd,
              displayBreak,
              { showZeroBreak: actualStateNorm === "different" },
            )}
          </span>
        </div>
      )}
      <div className="shift-actual-cell__chip-row">
        {clickable ? (
          <button
            type="button"
            onClick={onModify}
            className="shift-actual-cell__chip shift-actual-cell__chip--clickable"
            style={{ backgroundColor: chipColor }}
          >
            {chipLabel}
          </button>
        ) : (
          <span
            className="shift-actual-cell__chip"
            style={{ backgroundColor: chipColor }}
          >
            {chipLabel}
          </span>
        )}
      </div>
    </div>
  );
}
